import type { TherapyRxWriteDto } from "../../../api-client";

/**
 * A row as the Schnelltext parser (`handleParseInput` via `EditTable`) leaves it.
 *
 * `therapy` is the Heilmittel EXACTLY as typed — "KG", "KGG", "MT" — never a
 * resolved catalog row. That distinction is the whole point since MD-232: a
 * prescription line names the prescribed TERM, and the billable line item is
 * picked per slot at scheduling from the term, the Rx tarif group, the
 * discipline and the therapist's credentials.
 */
export interface QuickRxRow {
  therapy?: string | null;
  amount?: string | number | null;
  frequency?: string | null;
  /** What the EditTable Doppelbehandlung checkbox writes (2 = doubled). */
  unitsPerDay?: number | null;
  /** Legacy doubling flag — older callers/tests only; `unitsPerDay` wins. */
  isDoubled?: boolean | null;
  isHomeVisit?: boolean | null;
}

/**
 * Shown when a row is missing one of the three fields a line cannot exist
 * without. Kept as the message the quick-add dialog has always surfaced.
 */
export const QUICK_RX_INCOMPLETE_ROW_MESSAGE =
  "Bitte fügen Sie die Behandlung hinzu, um das Rezept zu speichern/bearbeiten " +
  "(Therapie, Menge, Häufigkeit sind Pflichtfelder) und deaktivieren Sie auch die " +
  "Zelle nach der Bearbeitung!";

/**
 * Schnelltext row -> the line shape the server ACCEPTS (`TherapyRxWriteDto`).
 *
 * Why this exists as its own function: the quick-add path used to build a
 * legacy line here by hand — `therapy: { id, abbreviation }`, with the id
 * resolved client-side from `abbreviation + tarif group`. Neither field is part
 * of the write contract any more, so `toTherapyRxWriteDto` dropped both on the
 * way out and every quick-add save reached the server naming no term at all.
 * The server then answered, correctly but opaquely, that the Heilmittel
 * "unbekannt" could not be mapped into the tarif group.
 *
 * `isHomeVisit` rides along for the caller's benefit — the Rx carries it, not
 * the line, and `toTherapyRxWriteDto` drops it.
 *
 * @throws Error with {@link QUICK_RX_INCOMPLETE_ROW_MESSAGE} for a row that
 *   names no therapy, no frequency or no amount.
 */
export type QuickRxWriteLine = TherapyRxWriteDto & { isHomeVisit?: boolean | null };

export const toQuickRxWriteLine = (row: QuickRxRow): QuickRxWriteLine => {
  const rawText = (row.therapy ?? "").toString().trim();
  const frequency = (row.frequency ?? "").toString().trim();
  const amount =
    row.amount === "" || row.amount === null || row.amount === undefined
      ? null
      : Number(row.amount);

  if (!rawText || !frequency || amount === null || !Number.isFinite(amount) || amount <= 0) {
    throw new Error(QUICK_RX_INCOMPLETE_ROW_MESSAGE);
  }

  return {
    // No `id`: every line of a brand-new Rx is a new line, and the write
    // contract wants the field omitted rather than filled with a client uuid.
    rawText,
    amount,
    // Doubling is `unitsPerDay` on an ordinary line now. The EditTable
    // checkbox writes `unitsPerDay` directly; `isDoubled` is only honoured as
    // a legacy fallback so older row shapes keep working.
    unitsPerDay: row.unitsPerDay ?? (row.isDoubled ? 2 : 1),
    // The server resolves the frequency by its text.
    frequency: { id: frequency, text: frequency } as TherapyRxWriteDto["frequency"],
    isHomeVisit: row.isHomeVisit ?? null,
  };
};
